import { compileCreatureAttacks } from './creature-attacks.js';
import { C1_CONTRACT, c1ProvisionalStatusChainId } from './possessor-compile.js';
import { readCreatureStatuses } from './creature-status-reader.js';
import { readItemField } from './item-field-reader.js';
import { encounterCreatureField } from '../../shared/array-types.js';
import { findInventoryRow } from '../inventory/flag-adjust.js';
import { readInventoryField } from './inventory-raw-readers.js';
import { createIntegerRollupCompiler } from './integer-layer-rollup.js';
import { rowLayerNames } from './row-layer-names.js';
import type { MemberStatusStep } from './member-status-recompile.js';

type Row = Record<string, unknown>;
const record = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
export const STATUS_ATTACK_BUDGET_KEYS = ['attacks', 'combat', 'modifiers', 'modifier_sources'] as const;
export type StatusAttackBudget = Partial<Record<typeof STATUS_ATTACK_BUDGET_KEYS[number], Row>>;
export const ATTACK_WINDOW_LIFETIMES = ['duration_type', 'effect_ends_on_target_start_of_turn', 'effect_ends_on_target_end_of_turn',
    'effect_ends_on_start_of_caster_next_turn', 'effect_ends_on_end_of_caster_next_turn'] as const;
const WINDOW_LAYERS = new Set(['extra_attack', 'roll_type_adv', 'roll_type_dis', 'roll_type_against_adv', 'roll_type_against_dis',
    'unenchantable', 'cost_gp', 'ability_charges', 'recharge_range_begin', 'recharge_range_end',
    'status_effect_applied_to_target_after_cast',
    ...ATTACK_WINDOW_LIFETIMES, 'duration_int', 'triggers_when_caster_makes_roll_type', 'triggers_on_hit', 'triggers_on_miss',
    'triggers_on_critical_hit', 'triggers_on_not_a_critical_hit', 'triggers_on_kill', 'triggers_on_roll_type_against_the_caster', 'ap_change_during_turn']);
/** A definition's layer names, structure and `_`-prefixed object-API metadata excluded (shared enumeration — execrhythm28b:
 * a Powerful hydrated through object_inc.php carries `_pl_stash`, which is not a window layer). */
export const statusDefinitionLayerNames = rowLayerNames;
/** Triggered extra attacks without an authored lifetime receive an end-of-turn
 * status. The ordinary weapon_attack counter spends the persisted turn budget. */
export function isAttackPassiveWindow(def: Row | null): boolean {
    if (!def || readItemField(def, 'item_type') !== 'passive_ability') return false;
    const keys = statusDefinitionLayerNames(def);
    return (ATTACK_WINDOW_LIFETIMES.some(key => keys.includes(key))
        || keys.includes('extra_attack') && keys.some(key => key.startsWith('triggers_')))
        && keys.every(key => WINDOW_LAYERS.has(key) || /^requires_skill_tier_\d+$/.test(key));
}

/** Powerful's percentage uses the native integer rollup, including its source
 * ledger, so add/reapply/expiry preserve other contributions without double folding. */
export function isDamageOutputStatus(def: Row | null): boolean {
    if (!def || readItemField(def, 'item_type') !== 'status_effect') return false;
    const keys = statusDefinitionLayerNames(def);
    return keys.includes('all_damage_output_changed_by_percentage')
        && keys.every(key => key === 'all_damage_output_changed_by_percentage' || ATTACK_WINDOW_LIFETIMES.some(lifetime => lifetime === key)
            || key === 'duration_int');
}
export function changesStatusAttackBudget(def: Row | null): boolean {
    return isDamageOutputStatus(def) || !!def && isAttackPassiveWindow(def) && statusDefinitionLayerNames(def).includes('extra_attack');
}

/** Native compiler readers shared by focused status-removal projections. */
export function statusProjectionDependencies(row: Row, definition: (id: string) => Row | null) {
    const ownId = String(encounterCreatureField(row.encounter_creatures, 'item_id') ?? '').toUpperCase();
    const resolve = (id: string): Row => {
        // Lean catalogs omit an empty layer bucket (Awake is a common example).
        const held = record(row.catalog)?.[id.toUpperCase()];
        const def = id.toUpperCase() === ownId ? row : record(held) ?? definition(id);
        if (!def) throw new Error('def-missing:' + id);
        return def;
    };
    const instances = new Map<string, Row | null>();
    const inventoryInstance = (id: string): Row | null => {
        const key = id.toUpperCase();
        if (instances.has(key)) return instances.get(key)!;
        const thin = findInventoryRow(row, id);
        if (!thin) { instances.set(key, null); return null; }
        const def = resolve(String(readInventoryField(thin, 'inventory_item_id') ?? ''));
        // The layer guard needs the same full view as the possessor compiler's fatInstance.
        // Borrow definition payloads: these readers never write inventory or definitions.
        const full: Row = { ...thin, items: { ...record(def.items), ...record(thin.items) },
            item_layer_types: def.item_layer_types ?? {}, ...(def.catalog ? { catalog: def.catalog } : {}) };
        const defined = record(def.computed), stored = record(thin.computed);
        if (record(defined?.vitality)) full.computed = { ...defined, ...stored,
            vitality: { ...record(defined?.vitality), ...record(stored?.vitality) } };
        for (const bucket of C1_CONTRACT.grants.inventoryAbilityTypes)
            if (full[bucket] === undefined && def[bucket] !== undefined) full[bucket] = def[bucket];
        instances.set(key, full);
        return full;
    };
    const sourceType = (id: string): string => {
        const key = id.toUpperCase();
        if (String(readItemField(record(row.player_characters), 'item_id') ?? '').toUpperCase() === key) return 'player_character';
        if (String(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase() === key) return 'creature';
        if (C1_CONTRACT.rollup.allEssences.some(essence => essence.item_id.toUpperCase() === key)) return 'essence';
        return String(readItemField(inventoryInstance(id) ?? resolve(id), 'item_type') ?? '');
    };
    return { ...C1_CONTRACT.rollup, catalogRows: C1_CONTRACT.catalogRows,
        metadata: C1_CONTRACT.rollup, abilityTypes: C1_CONTRACT.abilityTypes, skillEntries: C1_CONTRACT.skillEntries,
        vitalityStatusIds: C1_CONTRACT.vitalityStatusIds, inventoryAbilityTypes: C1_CONTRACT.rollup.inventoryAbilityTypes,
        equipmentSlots: C1_CONTRACT.equipmentSlots, aggregationStrategy: (name: string) => C1_CONTRACT.catalogRows[name]?.aggregation_strategy ?? 'host_wins',
        resolveCarrierRef: resolve, queryFullItem: resolve, queryTemplateDefinition: resolve,
        resolveInventoryInstance: inventoryInstance, resolveItemName: (id: string) => readItemField(inventoryInstance(id) ?? resolve(id), 'item_name') ?? '',
        queryItemType: sourceType,
        conditionSource: (kind: string) => { throw new Error('status-attacks:condition-context-not-held:' + kind); } };
}

/** Reproject only the affected native combat clusters, avoiding unrelated warm
 * skill/AC revalidation when an attack window or percentage status changes. */
export function projectStatusAttackBudget(row: Row, steps: MemberStatusStep[], definition: (id: string) => Row | null):
    { ok: true; budget: StatusAttackBudget } | { ok: false; reason: string } {
    let held = Object.values(readCreatureStatuses(row, 'target', definition)).map(value => value as Row);
    for (const [index, step] of steps.entries()) {
        if (step.kind === 'remove') held = held.filter(s => String(readItemField(s, 'status_id') ?? '').toUpperCase() !== step.statusId.toUpperCase());
        else held = [...held, { status_id: step.statusId ?? c1ProvisionalStatusChainId(index), status_item_id: step.statusItemId,
            status_target_id: encounterCreatureField(row.encounter_creatures, 'encounter_creature_id'), status_caster_id: step.casterId,
            status_stack: step.stack ?? 1, status_arr: definition(step.statusItemId) }];
    }
    const host = { ...row, statuses: { ...record(row.statuses), target: held } };
    try {
        const deps = statusProjectionDependencies(row, definition);
        const budget: StatusAttackBudget = {};
        if (steps.some(step => isAttackPassiveWindow(definition(step.statusItemId)))) {
            const result = compileCreatureAttacks(host, deps);
            if (!result.ok) return result;
            budget.attacks = record(result.attacks) ?? {}; budget.combat = record(result.combat) ?? {};
        }
        if (steps.some(step => isDamageOutputStatus(definition(step.statusItemId)))) {
            const compiled = createIntegerRollupCompiler({ ...deps,
                metadata: { ...C1_CONTRACT.rollup, integerLayers: ['all_damage_output_changed_by_percentage'] } }).compile(host);
            const creature = record(record((compiled as Row).computed)?.creature);
            budget.modifiers = record(creature?.modifiers) ?? {};
            budget.modifier_sources = record(creature?.modifier_sources) ?? {};
        }
        return { ok: true, budget };
    } catch (error) { return { ok: false, reason: 'attack-budget:' + String(error instanceof Error ? error.message : error) }; }
}
